import { formatCount } from "@/lib/conversation-count";
import { formatCop } from "@/lib/credits";
import type { AccountStanding, GraceStanding, PausedReason } from "@/lib/account-standing";

export const STANDING_TIME_ZONE = "America/Bogota";

export const GRACE_ACTION_LABEL = "Resolver ahora";
export const PAUSED_ACTION_LABEL = "Reactivar la IA";
export const SUPPORT_ACTION_LABEL = "Escribir a soporte";
export const REAUTHORIZE_SUBJECT = "Autorizar de nuevo el cobro";
export const ASK_ADMIN_MESSAGE = "Pídele a un administrador que lo resuelva.";
export const NOTIFY_ADMIN_LABEL = "Avisar a un administrador";

const dayFormat = new Intl.DateTimeFormat("es-CO", {
  timeZone: STANDING_TIME_ZONE,
  weekday: "long",
  day: "numeric",
  month: "long",
});

export function formatStandingDate(date: Date): string {
  const parts = Object.fromEntries(dayFormat.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.weekday} ${parts.day} de ${parts.month}`;
}

export interface StandingBannerCopy {
  tone: "grace" | "paused";
  message: string;
  actionLabel: string;
}

const TEAM_CAN_ANSWER = "Tus clientes siguen escribiendo: tu equipo puede responderles desde aquí.";

function pausedReasonSentences(reason: PausedReason, amount: number | null): string[] {
  const owed = owedSentence(amount);
  switch (reason) {
    case "payment_overdue":
    case "payment_failed":
      return [amount !== null && amount > 0 ? `Tienes un pago vencido de ${formatCop(amount)}.` : "Tienes un pago vencido."];
    case "credits_exhausted":
      return [amount !== null && amount > 0
        ? `Tienes un saldo pendiente de ${formatCop(amount)}: tu próxima recarga lo paga primero.`
        : "Se agotó tu saldo de créditos."];
    case "over_limit":
      return ["Superaste el límite de conversaciones de tu plan."];
    case "prepaid_expired":
      return ["Tu plan prepagado venció.", ...(owed ? [owed] : [])];
    case "authorization_lost":
      return [
        "Cancelaste en Mercado Pago la autorización de cobro por uso: para que vuelva a responder hay que autorizar de nuevo el cobro con soporte.",
        ...(owed ? [owed] : []),
      ];
    default:
      return owed ? [owed] : [];
  }
}

function owedSentence(amount: number | null): string | null {
  return amount !== null && amount > 0 ? `Tienes un pago pendiente de ${formatCop(amount)}.` : null;
}

function graceOpening(standing: GraceStanding): string {
  if (standing.reason !== "over_limit") return owedSentence(standing.amountOwed) ?? "Tienes un pago pendiente.";
  return standing.conversationLimit !== null
    ? `Superaste las ${formatCount(standing.conversationLimit)} conversaciones de tu plan.`
    : "Superaste las conversaciones de tu plan.";
}

export function standingBannerCopy(standing: AccountStanding): StandingBannerCopy | null {
  if (standing.kind === "grace") {
    const opening = graceOpening(standing);
    const until = standing.aiAnswersUntil
      ? `Tu IA sigue respondiendo hasta el ${formatStandingDate(standing.aiAnswersUntil)}.`
      : "Tu IA sigue respondiendo por ahora.";
    return {
      tone: "grace",
      message: `${opening} ${until} Después se pausa; tu equipo podrá seguir atendiendo.`,
      actionLabel: GRACE_ACTION_LABEL,
    };
  }
  if (standing.kind === "paused") {
    const since = standing.pausedSince
      ? `Tu IA está en pausa desde el ${formatStandingDate(standing.pausedSince)}.`
      : "Tu IA está en pausa.";
    return {
      tone: "paused",
      message: [since, ...pausedReasonSentences(standing.reason, standing.amountOwed), TEAM_CAN_ANSWER].join(" "),
      actionLabel: standing.action === "support" ? SUPPORT_ACTION_LABEL : PAUSED_ACTION_LABEL,
    };
  }
  return null;
}

export const TEAM_KEEPS_ANSWERING = "Tu equipo sigue atendiendo en la bandeja.";

export const PAUSED_FOOTER =
  "Mientras tanto: la bandeja, contactos y reservas funcionan. En pausa: IA, campañas, recontactos, nuevos agentes y nuevas integraciones.";

export const GRACE_FOOTER =
  "Por ahora no hay nada en pausa: la IA, las campañas y los recontactos siguen funcionando. La bandeja, contactos y reservas funcionan siempre, también si la cuenta se pausa.";

export interface ResolveStatusCopy {
  tone: "ok" | "grace" | "paused" | "closed";
  title: string;
  reason: string;
  footer: string | null;
}

export function resolveStatusCopy(standing: AccountStanding, orgName?: string | null): ResolveStatusCopy {
  const subject = orgName ? `La IA de ${orgName}` : "Tu IA";
  if (standing.kind === "paused") {
    const since = standing.pausedSince ? `Desde el ${formatStandingDate(standing.pausedSince)}.` : null;
    return {
      tone: "paused",
      title: `${subject} está en pausa`,
      reason: [since, ...pausedReasonSentences(standing.reason, standing.amountOwed)].filter(Boolean).join(" "),
      footer: PAUSED_FOOTER,
    };
  }
  if (standing.kind === "grace") {
    return {
      tone: "grace",
      title: standing.aiAnswersUntil
        ? `${subject} sigue respondiendo hasta el ${formatStandingDate(standing.aiAnswersUntil)}`
        : `${subject} sigue respondiendo por ahora`,
      reason: `${graceOpening(standing)} Después la IA se pausa.`,
      footer: GRACE_FOOTER,
    };
  }
  if (standing.kind === "closed") {
    return { tone: "closed", title: "Tu cuenta está cerrada", reason: "Escríbenos para revisar el caso.", footer: null };
  }
  return { tone: "ok", title: "Tu cuenta está al día", reason: "No hay nada pendiente por resolver.", footer: null };
}
